import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "@/index.css";
import "@/i18n";
import App from "@/App";

// Sentry — best-effort init; no-op if DSN env var is missing.
// Catches uncaught React errors, network failures, and unhandled promises
// across the entire app so the next "trouble connecting" bug shows up in
// our dashboard within seconds instead of waiting for a user report.
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV || 'production',
    tracesSampleRate: 0.05,
    replaysSessionSampleRate: 0,   // no session replay by default (privacy + cost)
    replaysOnErrorSampleRate: 0.1,
    sendDefaultPii: false,
  });
}

// Loading fallback for i18n
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--isp-page, #FFFFFF)' }}>
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#002FA7]" />
  </div>
);

// Service worker registration lives in /public/index.html so it can run
// before the React bundle parses. We used to register a SECOND SW
// (`sw-push.js`) here, which competed with `service-worker.js` at the same
// scope and caused the app to flip between old and new builds on refresh.
// Push notifications are now handled inside `service-worker.js`, so this
// extra registration is no longer needed.

// Initialize Capacitor mobile app features
import('./utils/capacitor').then(({ initMobileApp, storeAppVersion }) => {
  initMobileApp();
  storeAppVersion();
}).catch(() => {});

const root = ReactDOM.createRoot(document.getElementById("root"));
const AppWithBoundary = SENTRY_DSN
  ? Sentry.withErrorBoundary(App, { fallback: <LoadingFallback /> })
  : App;
root.render(
  <React.StrictMode>
    <Suspense fallback={<LoadingFallback />}>
      <AppWithBoundary />
    </Suspense>
  </React.StrictMode>,
);
